import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

type BrandLogoProps = {
  /** Rota do clique no logo; use `null` para somente imagem (sem link). */
  to?: string | null;
  className?: string;
  showTagline?: boolean;
};

/** Cache do conteudo SVG para evitar fetch repetido */
let svg_cache: string | null = null;

/**
 * Marca FormataFacil com mascote raia.
 * O SVG usa currentColor com fill-opacity variado para manter
 * as tonalidades originais (esquerda clara, manchas escuras)
 * e ao mesmo tempo seguir a cor do tema (--primary).
 */
export function BrandLogo({ to = '/', className = '', showTagline = false }: BrandLogoProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [loaded, set_loaded] = useState(!!svg_cache);

  useEffect(() => {
    if (svg_cache) {
      if (ref.current) ref.current.innerHTML = svg_cache;
      set_loaded(true);
      return;
    }
    let cancelled = false;
    void fetch('/arraia.svg')
      .then((r) => r.text())
      .then((text) => {
        if (cancelled) return;
        svg_cache = text;
        if (ref.current) ref.current.innerHTML = text;
        set_loaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  const content = (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <span
        ref={ref}
        role="img"
        aria-label="FormataFácil"
        className="block h-8 w-8 sm:h-9 sm:w-9 shrink-0 [&>svg]:w-full [&>svg]:h-full"
        style={{ filter: 'var(--brand-logo-filter, none)' }}
      />
      {showTagline && (
        <span className="hidden sm:flex flex-col pl-1 text-left leading-tight">
          <span className="text-sm font-semibold tracking-tight text-foreground">
            Formata<span className="text-primary">Fácil</span>
          </span>
          <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Documentos acadêmicos e profissionais
          </span>
        </span>
      )}
    </span>
  );

  if (to === null) {
    return content;
  }

  return (
    <Link to={to ?? '/'} className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md">
      {content}
    </Link>
  );
}
